import AxiosInstance from '../config/AxiosInstance';

const BASE = '/api/inventory/inventory-counts';

/** All products (1), chosen products (2), by attribute (3), by group (4). */
export const COUNT_TYPE = { All: 1, Individual: 2, Attribute: 3, Group: 4 } as const;

export interface CountsOverviewQuery {
  from?: string;
  to?: string;
  status?: string;
  type?: string;
  search?: string;
  locationId?: string;
  page: number;
  limit: number;
}

type Named = { id: string; name: string | null };

export interface CountsOverviewRow {
  id: string;
  internalNumber: number;
  statusId: number;
  type: number;
  reason: Named | null;
  location: Named | null;
  createdAt: string;
  createdBy: Named | null;
  approvedAt: string | null;
  approvedBy: Named | null;
  lines: number;
  counted: number;
}

export interface CountsOverview {
  rows: CountsOverviewRow[];
  count: number;
  page: number;
  limit: number;
  statusCounts: Record<string, number>;
}

/** A unit as the document sends it: the product unit's factor, and its UOM's. */
export interface CountUnit {
  id: string;
  isBase: boolean;
  conversionFactor: number | string | null;
  uom: { uomName: string | null; conversionFactor: number | string | null } | null;
}

export interface CountDocumentLine {
  id: string;
  productId: string;
  sku: string;
  productName: string;
  group: string;
  productUOMId: string | null;
  units: CountUnit[];
  /** Line units; null when not counted. */
  countedQty: number | null;
  approvedQty: number | null;
  /** Base units: the site's stock when the count was sent. */
  expectedQty: number | null;
  /** Base units the approval moves (or moved). */
  varianceQty: number | null;
  /** Per line unit. */
  cost: number;
  /** Base units at the site now. */
  onHand: number;
}

export interface CountDocument {
  count: {
    id: string;
    internalNumber: number;
    statusId: number;
    type: number;
    reason: Named | null;
    location: Named | null;
    createdAt: string;
    createdBy: Named | null;
    approvedAt: string | null;
    approvedBy: Named | null;
    updatedAt: string;
  };
  lines: CountDocumentLine[];
  viewer: { canApprove: boolean; isCreator: boolean };
}

export interface NewCountPayload {
  locationId: string;
  type: number;
  reasonId: string;
  productGroupIds?: string[];
  attributeIds?: string[];
}

export interface CountLinesPayload {
  lines: { id?: string; productId: string; productUOMId?: string; countedQty?: number | null; approvedQty?: number | null }[];
  removedIds?: string[];
}

const getCountsOverview = async (query: CountsOverviewQuery) => {
  const { data } = await AxiosInstance.get(`${BASE}/overview`, { params: query });
  return { data };
};

/** Lines for every countable product in scope are made by the API; an individual count starts empty. */
const createCount = async (payload: NewCountPayload) => {
  const { data } = await AxiosInstance.post(`${BASE}/new`, payload);
  return { data };
};

const getCountDocument = async (id: string) => {
  const { data } = await AxiosInstance.get(`${BASE}/${id}/document`);
  return { data };
};

const saveCountLines = async (id: string, payload: CountLinesPayload) => {
  const { data } = await AxiosInstance.put(`${BASE}/${id}/lines`, payload);
  return { data };
};

/** send, approve, recount, reject or cancel; each answers with the document. */
const countAction = async (id: string, action: 'send' | 'approve' | 'recount' | 'reject' | 'cancel') => {
  const { data } = await AxiosInstance.post(`${BASE}/${id}/${action}`);
  return { data };
};

export { getCountsOverview, createCount, getCountDocument, saveCountLines, countAction };
